import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { getDeliveries, getDeliveryDetail } from "../../api/deliveryCompanyService";
import {
  DeliveryStatusColors,
  DeliveryStatusLabels,
  OrderStatusLabels,
  formatCurrency,
  formatDate,
} from "../Admin/constants";

export default function Orders() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderNumberInput, setOrderNumberInput] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setOrderNumber(orderNumberInput.trim());
      setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(handle);
  }, [orderNumberInput]);

  useEffect(() => {
    setLoading(true);
    getDeliveries({
      status: statusFilter || undefined,
      orderNumber: orderNumber || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load orders. Is the API running?"))
      .finally(() => setLoading(false));
  }, [statusFilter, orderNumber, paginationModel]);

  const columns = useMemo(
    () => [
      { field: "orderNumber", headerName: "Order #", width: 260 },
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 150 },
      { field: "customerName", headerName: "Customer", flex: 1, minWidth: 150 },
      {
        field: "pickupCity",
        headerName: "From",
        width: 170,
        valueGetter: (v, row) => row.pickupCity || row.pickupAddressText || "-",
      },
      {
        field: "dropoffCity",
        headerName: "To",
        width: 170,
        valueGetter: (v, row) => `${row.dropoffCity}, ${row.dropoffStreet}`,
      },
      { field: "driverName", headerName: "Driver", width: 150, valueGetter: (v) => v || "Unassigned" },
      {
        field: "status",
        headerName: "Status",
        width: 160,
        renderCell: (params) => (
          <Chip size="small" label={DeliveryStatusLabels[params.value]} color={DeliveryStatusColors[params.value]} />
        ),
      },
      {
        field: "orderPrice",
        headerName: "Order Price",
        width: 130,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "deliveryFee",
        headerName: "Delivery Fee",
        width: 130,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "orderCreatedAt", headerName: "Ordered", width: 170, valueFormatter: (v) => formatDate(v) },
    ],
    []
  );

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Orders
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <TextField
          size="small"
          label="Search by order #"
          value={orderNumberInput}
          onChange={(e) => setOrderNumberInput(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(DeliveryStatusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.deliveryId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => setSelectedDeliveryId(params.row.deliveryId)}
          sx={{ border: "none", "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      <OrderDetailDialog deliveryId={selectedDeliveryId} onClose={() => setSelectedDeliveryId(null)} />
    </Box>
  );
}

function OrderDetailDialog({ deliveryId, onClose }) {
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!deliveryId) {
      setOrder(null);
      setError("");
      return;
    }
    let active = true;
    getDeliveryDetail(deliveryId)
      .then((data) => {
        if (active) setOrder(data);
      })
      .catch(() => {
        if (active) setError("Could not load this order.");
      });
    return () => {
      active = false;
    };
  }, [deliveryId]);

  const open = Boolean(deliveryId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {order ? order.orderNumber : "Order Details"}
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && !order && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {order && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Items
                  </Typography>
                  <List disablePadding>
                    {order.items.map((item, idx) => (
                      <ListItem key={idx} disableGutters divider={idx < order.items.length - 1}>
                        <ListItemAvatar>
                          <Avatar src={item.productImageUrl || undefined} variant="rounded" />
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.productName}
                          secondary={
                            item.variantName ? `${item.variantName} • Qty ${item.quantity}` : `Qty ${item.quantity}`
                          }
                        />
                        <Typography fontWeight={600}>{formatCurrency(item.unitPrice * item.quantity)}</Typography>
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack spacing={0.75}>
                    <SummaryRow label="Subtotal" value={formatCurrency(order.subTotal)} />
                    <SummaryRow label="Delivery Fee" value={formatCurrency(order.deliveryFee)} />
                    <SummaryRow label="Discount" value={`- ${formatCurrency(order.discountAmount)}`} />
                    <SummaryRow label="Total" value={formatCurrency(order.totalAmount)} bold />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Order Status History
                  </Typography>
                  <Stepper orientation="vertical">
                    {order.statusHistory.map((h, idx) => (
                      <Step key={idx} active completed>
                        <StepLabel>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body2" fontWeight={600}>
                              {OrderStatusLabels[h.status]}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(h.changedAt)}
                            </Typography>
                          </Stack>
                          {h.note && (
                            <Typography variant="caption" color="text.secondary">
                              {h.note}
                            </Typography>
                          )}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Paper>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Order Status
                  </Typography>
                  <Chip label={OrderStatusLabels[order.orderStatus]} size="small" sx={{ mb: 1 }} />
                  <DetailRow label="Ordered" value={formatDate(order.orderCreatedAt)} />
                  {order.notes && <DetailRow label="Notes" value={order.notes} />}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Customer
                  </Typography>
                  <Stack spacing={0.75}>
                    <DetailRow label="Name" value={order.customerName} />
                    <DetailRow label="Phone" value={order.customerPhone || "-"} />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Pickup (Store)
                  </Typography>
                  <Stack spacing={0.75}>
                    <DetailRow label="Store" value={order.storeName} />
                    <DetailRow label="Address" value={order.pickupCity || order.pickupAddressText || "-"} />
                  </Stack>
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Dropoff (Customer)
                  </Typography>
                  <Typography variant="body2">
                    {order.dropoffStreet} {order.dropoffBuildingNumber}
                    {order.dropoffApartmentNumber ? `, Apt ${order.dropoffApartmentNumber}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.dropoffCity}
                  </Typography>
                  {order.dropoffAdditionalDetails && (
                    <Typography variant="caption" color="text.secondary">
                      {order.dropoffAdditionalDetails}
                    </Typography>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Delivery
                  </Typography>
                  <Stack spacing={0.75}>
                    <Box>
                      <Chip
                        size="small"
                        label={DeliveryStatusLabels[order.status]}
                        color={DeliveryStatusColors[order.status]}
                      />
                    </Box>
                    <DetailRow label="Driver" value={order.driverName || "Not yet assigned"} />
                    <DetailRow label="Assigned" value={formatDate(order.assignedAt)} />
                    <DetailRow label="Picked Up" value={formatDate(order.pickedUpAt)} />
                    <DetailRow label="Delivered" value={formatDate(order.deliveredAt)} />
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value, bold }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color={bold ? "text.primary" : "text.secondary"} fontWeight={bold ? 700 : 400}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>
        {value}
      </Typography>
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
